import type { SyncExceptionView } from "@kopi/contracts";
import {
  SYNC_EXCEPTION_STATUSES,
  SYNC_QUARANTINE_REASONS,
  type SyncExceptionResolution,
  type SyncExceptionStatus,
  type SyncQuarantineReason,
} from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { RadioGroup, RadioGroupItem } from "@kopi/ui/components/radio-group";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Textarea } from "@kopi/ui/components/textarea";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ClipboardCheckIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { offlineExceptions } from "@/i18n/messages/offline-exceptions";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletsQuery, PAGE } from "@/lib/queries";

const ALL = "all";
const KEY = ["offline-exceptions"] as const;
const RESOLUTIONS: SyncExceptionResolution[] = ["accepted", "rejected"];

const useExceptionMessages = () => useMessages(offlineExceptions);
type ExceptionsT = ReturnType<typeof useExceptionMessages>["t"];

const reasonLabel = (t: ExceptionsT, reason: string) =>
  (SYNC_QUARANTINE_REASONS as readonly string[]).includes(reason)
    ? t(`offlineExceptions.reason.${reason as SyncQuarantineReason}`)
    : t("offlineExceptions.reason.other", { code: reason });

/** The status filter lives in the URL; open cases are what a manager opens the page for. */
function validateSearch(search: Record<string, unknown>): { status: SyncExceptionStatus | typeof ALL } {
  const status = search.status;
  return status === ALL || SYNC_EXCEPTION_STATUSES.includes(status as SyncExceptionStatus)
    ? { status: status as SyncExceptionStatus | typeof ALL }
    : { status: "open" };
}

/** An outlet's cases, newest first, a page at a time (CODING_STANDARDS §4). */
const exceptionsQuery = (outletId: string, status: SyncExceptionStatus | undefined) =>
  infiniteQueryOptions({
    queryKey: [...KEY, outletId, { status }],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId })["offline-exceptions"].get({
          query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}), ...(status ? { status } : {}) },
        }),
      ),
    enabled: Boolean(outletId),
  });

export const Route = createFileRoute("/_authed/offline-exceptions")({
  staticData: { section: "workspace", crumb: "nav.offlineExceptions" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: OfflineExceptionsPage,
});

const columnsFor = (t: ExceptionsT, money: (v: bigint) => string, when: (iso: string) => string) =>
  [
    {
      id: "sale",
      header: t("offlineExceptions.sale"),
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium">{reasonLabel(t, row.original.reason)}</span>
          <span className="truncate text-muted-foreground text-sm">
            {t("offlineExceptions.items", {
              count: row.original.itemCount,
              register: row.original.registerName,
            })}
          </span>
        </div>
      ),
    },
    {
      id: "cashier",
      header: t("offlineExceptions.cashier"),
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span>{row.original.cashierName ?? t("offlineExceptions.cashierUnknown")}</span>
          <span className="truncate text-muted-foreground text-sm">
            {t("offlineExceptions.syncedBy", { name: row.original.uploadedByName })}
          </span>
        </div>
      ),
    },
    {
      id: "occurred",
      header: t("offlineExceptions.occurred"),
      cell: ({ row }) => <time dateTime={row.original.occurredAt}>{when(row.original.occurredAt)}</time>,
    },
    {
      id: "status",
      header: t("offlineExceptions.status"),
      meta: { className: "md:w-44" },
      cell: ({ row }) => {
        const c = row.original;
        if (c.status !== "resolved")
          return <Badge variant="warning">{t(`offlineExceptions.status.${c.status}`)}</Badge>;
        return (
          <Badge variant={c.resolution === "accepted" ? "success" : "secondary"}>
            {c.resolution === "accepted"
              ? t("offlineExceptions.outcome.accepted", { receipt: c.receiptNumber ?? "" })
              : t("offlineExceptions.outcome.rejected")}
          </Badge>
        );
      },
    },
    {
      id: "amount",
      header: t("offlineExceptions.amount"),
      meta: { align: "end" },
      cell: ({ row }) => <span className="tabular-nums">{money(BigInt(row.original.total))}</span>,
    },
  ] satisfies DataTableColumn<SyncExceptionView>[];

/**
 * Offline reconciliation (R1-41, PRD §21 conflict policy): offline sales whose cash was taken but
 * whose authority, price, stock or shift couldn't be trusted at sync. A manager accepts one, recording
 * the sale under its original cashier, or rejects it; the evidence is kept either way.
 */
function OfflineExceptionsPage() {
  const { t, format } = useExceptionMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const reconcilable = outlets.filter((o) => canAt(scopes, "offline.reconcile", o.id));
  const [outletId, setOutletId] = useState(
    reconcilable.find((o) => o.id === current?.id)?.id ?? reconcilable[0]?.id ?? "",
  );
  const { status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useInfiniteQuery(exceptionsQuery(outletId, status === ALL ? undefined : status));
  const [resolving, setResolving] = useState<SyncExceptionView | null>(null);
  const columns = useMemo(
    () =>
      columnsFor(
        t,
        (v) => format.money(v),
        (iso) => format.dateTime(iso),
      ),
    [t, format],
  );

  if (reconcilable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("nav.offlineExceptions")} subtitle={t("offlineExceptions.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={t("nav.offlineExceptions")} subtitle={t("offlineExceptions.subtitle")} />
      <DataTable
        paging="pages"
        label={t("offlineExceptions.list")}
        columns={columns}
        query={list}
        getRowId={(c) => c.id}
        filters={
          <>
            {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
            <OptionSelect
              aria-label={t("outlet.heading")}
              className="md:w-56"
              options={reconcilable.map((o) => ({ value: o.id, label: o.name }))}
              value={outletId}
              onValueChange={setOutletId}
            />
            <OptionSelect
              aria-label={t("offlineExceptions.status")}
              className="md:w-56"
              options={[
                { value: ALL, label: t("offlineExceptions.status.all") },
                ...SYNC_EXCEPTION_STATUSES.map((s) => ({
                  value: s,
                  label: t(`offlineExceptions.status.${s}`),
                })),
              ]}
              value={status}
              onValueChange={(value) =>
                void navigate({
                  search: { status: value as SyncExceptionStatus | typeof ALL },
                  replace: true,
                })
              }
            />
          </>
        }
        rowActions={(c) =>
          c.status === "resolved"
            ? []
            : [
                {
                  label: t("offlineExceptions.resolve"),
                  icon: <ClipboardCheckIcon />,
                  onSelect: () => setResolving(c),
                },
              ]
        }
        empty={{ title: t("offlineExceptions.emptyTitle"), description: t("offlineExceptions.emptyHint") }}
      />
      {resolving && <ResolveDialog exception={resolving} onClose={() => setResolving(null)} />}
    </Page>
  );
}

/** One decision on a quarantined sale: accept it as rung up, or reject it. */
function ResolveDialog(props: { exception: SyncExceptionView; onClose: () => void }) {
  const { exception } = props;
  const { t, format } = useExceptionMessages();
  const queryClient = useQueryClient();
  const ids = { resolution: useId(), note: useId() };
  // A stalled acceptance (`resolving`) may still be rejected; the server refuses once its sale exists.
  const options = RESOLUTIONS;
  const [resolution, setResolution] = useState<SyncExceptionResolution>("accepted");
  const [note, setNote] = useState("");
  const valid = note.trim().length >= 3;
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1["offline-exceptions"]({ exceptionId: exception.id }).resolve.post({
          resolution,
          note: note.trim(),
          expectedRevision: exception.revision,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(t("offlineExceptions.resolved"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  const code = save.error instanceof ApiRequestError ? save.error.code : null;
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("offlineExceptions.resolveTitle")}
      description={reasonLabel(t, exception.reason)}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="resolve-offline-exception" disabled={!valid || save.isPending}>
          {t(save.isPending ? "offlineExceptions.saving" : "offlineExceptions.save")}
        </Button>
      }
    >
      <form id="resolve-offline-exception" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1 text-sm">
          <p className="font-medium tabular-nums">
            {t("offlineExceptions.summary", {
              total: format.money(BigInt(exception.total)),
              cash: format.money(BigInt(exception.cashTendered)),
              when: format.dateTime(exception.occurredAt),
            })}
          </p>
          <p className="text-muted-foreground">
            {t("offlineExceptions.attribution", {
              cashier: exception.cashierName ?? t("offlineExceptions.cashierUnknown"),
              uploader: exception.uploadedByName,
            })}
            {exception.reason === "employee_unverified" && ` ${t("offlineExceptions.unverifiedHint")}`}
          </p>
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium text-sm">{t("offlineExceptions.resolution")}</legend>
          <RadioGroup
            value={resolution}
            onValueChange={(value) => setResolution(value as SyncExceptionResolution)}
            className="flex flex-col gap-3"
          >
            {options.map((option) => (
              <div key={option} className="flex items-start gap-3">
                <RadioGroupItem id={`${ids.resolution}-${option}`} value={option} className="mt-1" />
                <Label
                  htmlFor={`${ids.resolution}-${option}`}
                  className="flex flex-col items-start gap-0.5 font-normal"
                >
                  <span className="font-medium">{t(`offlineExceptions.resolution.${option}`)}</span>
                  <span className="text-muted-foreground text-sm">
                    {t(`offlineExceptions.resolution.${option}Hint`)}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </fieldset>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.note}>{t("offlineExceptions.note")}</Label>
          <Textarea
            id={ids.note}
            maxLength={500}
            value={note}
            placeholder={t("offlineExceptions.notePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              code === "exception_revision_conflict" || code === "exception_resolved"
                ? "offlineExceptions.error.changed"
                : code === "sale_recorded"
                  ? "offlineExceptions.error.saleRecorded"
                  : code === "sale_refused" || code === "employee_unknown"
                    ? "offlineExceptions.error.cannotAccept"
                    : "offlineExceptions.error.failed",
            )}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
